"use client";

import { useCallback } from "react";
import { Track } from "@/src/lib/tracking";
import { getAdSourceFromStore } from "@utils/onboarding/adSourceStore";
import { overlapAttributionParams } from "../tracking/overlapAttribution";

/** PRD S1.5：登录成功后最多等 3s 取 `ad_source`，取不到按线上默认流程放行。 */
export const OVERLAP_ATTRIBUTION_WAIT_MS = 3_000;
const POLL_INTERVAL_MS = 100;

let timeoutReported = false;

/** 仅供单测重置「只报一次」的会话态。 */
export function resetOverlapAttributionForTest(): void {
  timeoutReported = false;
}

export interface WaitForAdSourceOptions {
  timeoutMs?: number;
  /** 读取当前 ad_source；默认读 store（内存为空时回落 cookie）。 */
  poll?: () => string | null;
}

/**
 * 非 hook 版，便于 entry gate（`useMemoizedFn` 内）与单测直接调用。
 * 取到值立即返回；超时返回 `null` 并**只上报一次** `overlap_attribution_timeout`。
 */
export async function waitForAdSource(
  options: WaitForAdSourceOptions = {},
): Promise<string | null> {
  const timeoutMs = options.timeoutMs ?? OVERLAP_ATTRIBUTION_WAIT_MS;
  const poll = options.poll ?? getAdSourceFromStore;
  const startedAt = Date.now();
  for (;;) {
    const value = poll();
    if (value) {
      return value;
    }
    if (Date.now() - startedAt >= timeoutMs) {
      break;
    }
    await new Promise<void>((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
  if (!timeoutReported) {
    timeoutReported = true;
    Track.event("overlap_attribution_timeout", overlapAttributionParams());
  }
  return null;
}

export const useOverlapAttribution = () => {
  const wait = useCallback(
    (options?: WaitForAdSourceOptions) => waitForAdSource(options),
    [],
  );
  return { waitForAdSource: wait };
};
